import React, { useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import type { BrandAsset } from "../api/brandAssets";

const ALLOWED_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_FILE_SIZE = 5 * 1024 * 1024;

type Props = {
  label: string;
  helperText?: string;
  assetType: BrandAsset["asset_type"];
  currentUrl: string | null | undefined;
  onUpload: (file: File) => void;
  onDelete: () => void;
  uploading?: boolean;
  deleting?: boolean;
};

const BrandAssetUpload: React.FC<Props> = ({
  label,
  helperText,
  currentUrl,
  onUpload,
  onDelete,
  uploading = false,
  deleting = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type || !ALLOWED_TYPES.has(file.type)) {
      setValidationError("Only PNG, JPEG and WebP images are supported.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setValidationError("File must be under 5 MB.");
      e.target.value = "";
      return;
    }

    setValidationError(null);
    onUpload(file);
    e.target.value = "";
  };

  const busy = uploading || deleting;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        {currentUrl && (
          <Avatar
            src={currentUrl}
            variant="rounded"
            sx={{ width: 56, height: 56 }}
          />
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: "none" }}
          onChange={handleFileChange}
          disabled={busy}
        />

        <Button
          variant="outlined"
          size="small"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          startIcon={uploading ? <CircularProgress size={14} /> : undefined}
        >
          {uploading ? "Uploading…" : currentUrl ? "Replace" : "Upload"}
        </Button>

        {currentUrl && (
          <Button
            variant="text"
            size="small"
            color="error"
            onClick={onDelete}
            disabled={busy}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            {deleting ? "Removing…" : "Remove"}
          </Button>
        )}
      </Stack>

      {validationError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {validationError}
        </Typography>
      )}

      {helperText && !validationError && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default BrandAssetUpload;
